<script setup lang="ts">
import type { GiftlyDesign } from '@consumer/types/serializers'
import { maskFromName } from '@consumer/assets/masks'
import { isHolidayDesign } from '@consumer/utils/design'
import santaPreviewRiveUrl from '@consumer/assets/images/customizer/santa-preview.riv?url'
import RivePreview from '@shared/components/Rive/canvas/RivePreview.vue'
import newCheckIcon from '@consumer/assets/icons/new/check.svg?svgicon'

const { design, holidays, isSelected, ariaLabel } = defineProps<{
  design?: GiftlyDesign
  isSelected?: boolean
  holidays?: boolean
  ariaLabel?: string
}>()

const isAnimatedSanta = $computed(
  () => design?.id === 'v3-holidays-2024-1',
)

const buttonAriaLabel = $computed(() => {
  if (ariaLabel) return ariaLabel
  if (!design) return undefined
  const friendly = design.id
    .replace(/^v3-/, '')
    .replace(/\bgcip\b/, 'card')
    .replace(/-/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
  return `Select ${friendly} design`
})
</script>

<template>
  <div
    class="design-button-wrapper flex items-center justify-center"
    :class="{ holidays: holidays || (design && isHolidayDesign(design)) }"
  >
    <button
      class="flex-shrink-0 flex items-center justify-center relative w-full rounded-full"
      :style="{
        aspectRatio: '1 / 1',
        boxShadow: `0px 1.846px 3.692px rgba(0, 0, 0, 0.15)`,
      }"
      type="button"
      :aria-label="buttonAriaLabel"
    >
      <i
        class="
        rounded-full w-full h-full flex items-center justify-center bg-center bg-contain
        outline-2 outline-primary-base hover:outline
        transition-all ease-in-out duration-200
      "
        :class="{ 'outline': isSelected }"
      >
        <slot>
          <div
            v-if="isAnimatedSanta"
            class="
              overflow-hidden
              relative w-full h-full
              rounded-full border-2 border-solid border-white
              pointer-events-none
            "
          >
            <RivePreview
              :riveUrl="santaPreviewRiveUrl"
              stateMachine="mainSM"
              artboard="mainArtboard"
              class="w-full h-full"
              :fallbackImageUrl="design?.backgroundImageUrl"
            />
          </div>
          <div
            v-else-if="design"
            class="overflow-hidden relative w-full h-full rounded-full border-2 border-solid border-white"
            style="background-position: center; background-size: cover;"
            :style="design.backgroundColor
              ? { backgroundColor: design.backgroundColor }
              : { backgroundImage: `url('${design.backgroundImageUrl}')` }
            "
          >
            <div
              class="giftly-image-v3--mask pointer-events-none"
              :style="{ color: design.maskColor, width: '150%', height: '100%' }"
              v-html="maskFromName(design.maskName || 'giftly-v3-mask-1')"
            />
          </div>
        </slot>

        <template v-if="isSelected">
          <div class="absolute inset-0 rounded-full bg-opacity-30 bg-black border-2 border-solid border-white"/>
          <GIcon
            :svg="newCheckIcon"
            color="white"
            size="24px"
            class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                  stroke-2 stroke-cap-round filter drop-shadow-md"
          />
        </template>

      </i>
    </button>
  </div>
</template>

<style scoped lang="scss">
.design-button-wrapper {
  padding: 4px;

  &.holidays {
    background: #b4eaa7;
  }

  &:nth-child(1 of .holidays),
  &:nth-child(5n + 1 of &) {
    border-bottom-left-radius: 9999px;
    border-top-left-radius: 9999px;
  }

  &:nth-last-child(1 of .holidays),
  &:nth-child(5n + 5 of &) {
    border-bottom-right-radius: 9999px;
    border-top-right-radius: 9999px;
  }
}
</style>
